// ═══ MOVING WORDS BETWEEN DICTIONARIES (owner ask 2026-10-03) ═══════════════
// The app's half of the shell's move: what "move" means here and how it is
// taken back. The rules (what leaving and joining mean, the inherited stance,
// the inverse op) are lib/move.mjs's; the write is the store's retag, which
// answers word by word. Nothing here draws: the selection, the bar, the picker
// and the toast are the shell's, and call these.
//
//   moveEntries(ids, to, { from })  → { to, from, moved, unchanged, failed, skipped, undo }
//   createDictionary(name)          → the new dictionary's id (its tag), or throws
//   moveIntoNew(name, ids, { from }) → createDictionary, then moveEntries
//   predict(ids, to, { from })      → the words as they would stand after it (the list folds the leavers first)
//   addEntries(ids, to)             → ADD TO: join `to`, leave nothing (lib/move.mjs planAdd)
//   addIntoNew(name, ids)           → found one and add to it
//   removeEntries(ids, { from })    → REMOVE FROM: leave `from` (the dictionary in force); every word keeps them
//
// A room's id is its TAG, as the atrium keys its elements; MASTER ('') is
// every word. `from` is the room the words are moved out of, by default the
// dictionary in force. undo() puts every moved word's tags (and stance) back
// as the graph held them before the move, and retires a dictionary the move
// itself founded if no word wears it afterwards.
import { MASTER, planMove, inverseOp, isNoop, applyTagOp } from '../../lib/move.mjs'

export { MASTER }

export function makeMover({ store, isAntiTag, ensureLexicon, retireLexicon, lens = MASTER }) {
  const settled = (to, from, plan, r, undo) => ({
    to, from,
    moved: r.done.map(d => d.id),
    unchanged: plan.unchanged,
    failed: r.failed,
    skipped: [...plan.skipped, ...r.skipped],
    undo,
  })

  const moveEntries = async (ids, to, { from = lens, founded = null } = {}) => {
    const plan = planMove(store.current(), ids, { to, from, isAnti: isAntiTag })
    const r = await store.retag(plan.ops)
    const back = r.done.map(d => inverseOp(d.before, d.op)).filter(op => !isNoop(op))
    let undone = null
    // the undo runs once; a second press is answered by the first's result
    const undo = () => undone || (undone = (async () => {
      const u = await store.retag(back)
      if (founded && !store.current().some(w => !w.owner && (w.tags || []).includes(founded.name))) retireLexicon(founded)
      return settled(from, to, { unchanged: [], skipped: [] }, u, null)
    })())
    return settled(to, from, plan, r, undo)
  }

  // the dictionary's node, through the same door the atrium's maker uses
  const found = async raw => {
    const name = String(raw || '').trim()
    if (!name) throw new Error('a dictionary needs a name')
    const lexicon = await ensureLexicon(name, {}, { strict: true })
    if (!lexicon) throw new Error(`could not make “${name}”`)
    return lexicon
  }
  const createDictionary = async name => (await found(name)).name

  const moveIntoNew = async (name, ids, opts = {}) => {
    const had = store.current().some(w => !w.owner && (w.tags || []).includes(String(name || '').trim()))
    const lexicon = await found(name)
    return moveEntries(ids, lexicon.name, { ...opts, founded: had || lexicon.existed ? null : lexicon })
  }

  const predict = (ids, to, { from = lens } = {}) => {
    const ops = new Map(planMove(store.current(), ids, { to, from, isAnti: isAntiTag }).ops.map(op => [op.id, op]))
    return store.current().map(w => {
      const op = ops.get(w.id)
      return op ? { ...w, tags: applyTagOp(w.tags, op), ...(op.stance ? { stance: op.stance } : {}) } : w
    })
  }

  // ADD TO and REMOVE FROM (owner call 2026-10-04): the same write and undo, planned as
  // lib/move.mjs's planAdd (from every word: nothing is left) and planRemove (into every word)
  const addEntries = (ids, to) => moveEntries(ids, to, { from: MASTER })
  const addIntoNew = (name, ids) => moveIntoNew(name, ids, { from: MASTER })
  const removeEntries = (ids, { from = lens } = {}) => {
    if (!from) return Promise.reject(new Error('every word keeps every word: there is no dictionary to remove them from'))
    return moveEntries(ids, MASTER, { from })
  }

  return { moveEntries, createDictionary, moveIntoNew, predict, addEntries, addIntoNew, removeEntries }
}
